import { iconIndex } from 'api/algolia';
import { Icons8SearchParams, Icons8SearchResponse, icons8Search } from 'libs/e1api/icons8';
import { IconColorOption } from 'types/icon.types';

import { restrictSearchableAttributes } from './usePaginatedIconSearch.config';
import { IconHit, IconSearchData, SearchApiResponse } from './usePaginatedIconSearch.types';

const DEFAULT_HITS_PER_PAGE_SEARCH = 36;
const DEFAULT_HITS_PER_PAGE_PACK = 36;

const parseKey = (key: string) => {
  const [, query, page, style] = key.split('_');
  return {
    query,
    page,
    style,
  };
};

/**
 * Fetch icons using Algolia search
 *
 * @param key [query]_[page]_[style]
 * @returns
 */
export const fetchAlgoliaIcons = (key: string) => {
  const { query, page, style } = parseKey(key);

  const attributeStyle = style === IconColorOption.All ? 'attr_style:' : `attr_style: ${style}`;
  const facetFilters = [attributeStyle, 'published:-0'];
  const hitsPerPage = DEFAULT_HITS_PER_PAGE_SEARCH;

  return iconIndex
    .search(query, { hitsPerPage, facetFilters, restrictSearchableAttributes, page: +page })
    .then((content) => content);
};

/**
 * Fetch icons using icons8 E1 search endpoint
 *
 * @param key [query]_[page]_[style]
 * @returns
 */
export const fetchIcons8Icons = (key: string) => {
  const { query, page, style } = parseKey(key);

  const icons8SearchParams: Icons8SearchParams = {
    // Remove special characters - E1 endpoint doesn't support special characters
    query: query.replace(/[^a-zA-Z0-9 ]/g, ''),
    limit: DEFAULT_HITS_PER_PAGE_SEARCH,
    offset: +page * DEFAULT_HITS_PER_PAGE_SEARCH,
  };

  // Only specify isColor filter if a filter is applied (i.e. not All)
  if (style !== IconColorOption.All) {
    icons8SearchParams.isColor = style === IconColorOption.Color;
  }

  return icons8Search(icons8SearchParams).then((content) => content);
};

const oldIconRegex = /^(fa3-icon|rjs[1-2]|vg-icon).*/;
const filterOutOldIcons = (icon: IconHit) => !oldIconRegex.test(icon.id);
const flattenHits = (d: SearchApiResponse) => d.hits;
export const parseAlgoliaIcons = (data: SearchApiResponse[]) => {
  if (!data) return [];

  return data.filter(Boolean).flatMap(flattenHits).filter(filterOutOldIcons);
};

/**
 * Flatten Icons8 search responses and filters out explicit, non-svg icons
 *
 * @param data
 * @returns
 */
export const parseIcons8Icons = (data: Icons8SearchResponse[]) => {
  if (!data) return [];

  // Same as E1 - filter out explicit icons
  const BLOCKED_CATEGORIES = ['Explicit', 'Sex Toy'];

  const icons: { [key: string]: boolean } = {};

  return data
    .filter(Boolean)
    .flatMap((d) => d.icons)
    .filter((icon) => {
      const includeIcon = icon.sourceFormat === 'svg' && !BLOCKED_CATEGORIES.includes(icon.category);

      if (includeIcon && !icons[icon.id]) {
        icons[icon.id] = true;
        return true;
      }

      return false;
    });
};

/**
 * Parses icons from each icon search response
 * For now, only from Algolia and Icons8
 * Returns normalized data to be rendered in the icon left menu
 *
 * @param algoliaData Algolia search response
 * @param icons8Data Icons8 search response
 * @returns IconSearchData[] - normalized icon data from the search responses
 */
export const parseAllIcons = (algoliaData: SearchApiResponse[], icons8Data: Icons8SearchResponse[]) => {
  let allIcons: IconSearchData[] = [];

  const algoliaIcons = allIcons.concat(
    parseAlgoliaIcons(algoliaData).map((icon) => ({
      id: icon.id,
      name: icon.name,
      permission: icon.type,
      isIllustration: icon.illustration === 1,
      isMono: icon.attr_style === 'mono',
    })),
  );
  const icons8Icons = allIcons.concat(
    parseIcons8Icons(icons8Data).map(({ id, commonName, isColor, platform, sourceFormat }) => ({
      id: id,
      name: commonName,
      isMono: !isColor,

      // No gating for icons8
      permission: 'default',

      // TODO: may need to update this when illustration icons are supported
      isIllustration: false,

      // TODO move to generic icons8 helper
      iconPath: `${platform}/${commonName}.${sourceFormat}`,
    })),
  );

  // Mix Algolia and Icons8 icons together
  // Algolia icons should render in the first 2 columns
  // Icons 8 should render in the last 2 columns
  const totalNumIcons = algoliaIcons.length + icons8Icons.length;
  let algoliaIdx = 0;
  let icons8Idx = 0;
  for (let i = 0; i < totalNumIcons; i++) {
    if (i % 4 < 2) {
      // No more algolia icons, concat the rest of the icons8 icons
      if (algoliaIdx > algoliaIcons.length - 1) {
        allIcons = allIcons.concat(icons8Icons.slice(icons8Idx));
        break;
      }
      allIcons[i] = algoliaIcons[algoliaIdx];
      algoliaIdx++;
    } else {
      // If no more icons8 icons, just concat the rest of the algolia icons
      if (icons8Idx > icons8Icons.length - 1) {
        allIcons = allIcons.concat(algoliaIcons.slice(algoliaIdx));
        break;
      }
      allIcons[i] = icons8Icons[icons8Idx];
      icons8Idx++;
    }
  }

  return allIcons;
};

/**
 * Fetches icons in a specific pack with pagination
 *
 * @param key string of form <PACK_ID>_<PAGE_NUMBER>
 * @returns
 */
export const fetchIconsByPack = (key: string) => {
  const { query: packId, page } = parseKey(key);

  const facetFilters = ['published:-0', `iconPacks:${packId}`];
  const hitsPerPage = DEFAULT_HITS_PER_PAGE_PACK;

  return iconIndex
    .search('', { hitsPerPage, facetFilters, restrictSearchableAttributes, page: +page })
    .then((content) => content);
};

/**
 * Check if the "All" icon style is included in the icon style options array
 *
 * @param iconColorOptions IconColorOption[]
 */
export const hasAllIconOption = (iconColorOptions: IconColorOption[]): boolean =>
  iconColorOptions.includes(IconColorOption.All);

/**
 * Check if there are more icons8 icons to load based on the last search response received
 *
 * @param lastIcons8Response The last Icons8 search response
 * @returns boolean
 */
export const hasMoreIcons8Icons = (lastIcons8Response: Icons8SearchResponse) => {
  if (!lastIcons8Response) return false;

  const totalNumPages = Math.ceil(lastIcons8Response.parameters.countAll / DEFAULT_HITS_PER_PAGE_SEARCH);
  const lastPage = Math.floor(lastIcons8Response.parameters.offset / DEFAULT_HITS_PER_PAGE_SEARCH);

  return lastPage < totalNumPages - 1;
};
